import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Search, PlusCircle, FileEdit, Trash, Eye, Clock, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { examsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import AddExamModal from "@/components/admin/AddExamModal";
import ViewExamModal from "@/components/admin/ViewExamModal";
import EditExamModal from "@/components/admin/EditExamModal";
import DeleteExamConfirmDialog from "@/components/admin/DeleteExamConfirmDialog";

const Exams = () => {
  const [searchTermMy, setSearchTermMy] = useState("");
  const [searchTermCompany, setSearchTermCompany] = useState("");
  const [pageMyExams, setPageMyExams] = useState(1);
  const [pageCompanyExams, setPageCompanyExams] = useState(1);
  const [limit] = useState(5);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  // Sorting states for both tables
  const [mySortField, setMySortField] = useState('');
  const [mySortOrder, setMySortOrder] = useState('asc');
  const [companySortField, setCompanySortField] = useState('');
  const [companySortOrder, setCompanySortOrder] = useState('asc');

  // Query for "My Exams" - API returns exams sorted by creation date (newest first)
  const {
    data: myExamsData,
    isLoading: isLoadingMy,
    refetch: refetchMy,
  } = useQuery({
    queryKey: ["exams", "my", pageMyExams, limit, searchTermMy],
    queryFn: () =>
      examsAPI.getAll({
        page: pageMyExams,
        limit,
        search: searchTermMy,
        filter: "my",
      }),
    keepPreviousData: true,
  });

  // Query for "Company Exams" - API returns exams sorted by creation date (newest first)
  const {
    data: companyExamsData,
    isLoading: isLoadingCompany,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ["exams", "company", pageCompanyExams, limit, searchTermCompany],
    queryFn: () =>
      examsAPI.getAll({
        page: pageCompanyExams,
        limit,
        search: searchTermCompany,
        filter: "company",
      }),
    keepPreviousData: true,
  });

  const refetchAll = () => {
    // Reset to page 1 to show newest exams first
    setPageMyExams(1);
    setPageCompanyExams(1);
    
    // Clear sorting to show default newest-first order
    setMySortField('');
    setMySortOrder('asc');
    setCompanySortField('');
    setCompanySortOrder('asc');
    
    // Refetch both sections
    refetchMy();
    refetchCompany();
  };

  // Sorting functions
  const handleMySort = (field) => {
    if (mySortField === field) {
      setMySortOrder(mySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setMySortField(field);
      setMySortOrder('asc');
    }
  };

  const handleCompanySort = (field) => {
    if (companySortField === field) {
      setCompanySortOrder(companySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setCompanySortField(field);
      setCompanySortOrder('asc');
    }
  };

  const getSortIcon = (field, currentSortField, currentSortOrder) => {
    if (currentSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return currentSortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortExams = (exams, sortField, sortOrder) => {
    // If no sorting is applied, return exams in their original order (newest first from API)
    // This ensures newly created exams appear at the top across all pages
    if (!sortField) return exams;

    return [...exams].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSearchMy = (e) => {
    e.preventDefault();
    setPageMyExams(1);
    refetchMy();
  };

  const handleSearchCompany = (e) => {
    e.preventDefault();
    setPageCompanyExams(1);
    refetchCompany();
  };

  const handleViewExam = (exam) => {
    setSelectedExam(exam);
    setIsViewModalOpen(true);
  };

//   const handleEditExam = (exam) => {
    
//   // if (!canEditExam(exam)) {
//   //   toast({
//   //     title: "Cannot edit exam",
//   //     description:
//   //       "This exam has assignments that are still pending or ongoing. You can only edit exams when all assignments are completed or disqualified.",
//   //     variant: "destructive",
//   //   });
//   //   return;
//   // }

//   setSelectedExam(exam);
//   setIsEditModalOpen(true);
// };

const handleEditExam = async (exam) => {
  try {
    const res = await examsAPI.canEdit(exam._id);

    if (!res.data.canEdit) {
      toast({
        title: "Cannot edit exam",
        description:
          "This exam has been assigned and not all candidates have completed, been disqualified, or missed the exam.",
        variant: "destructive",
      });
      return;
    }

    setSelectedExam(exam);
    setIsEditModalOpen(true); 


  } catch (error) {
    console.error("Edit check failed:", error);
    toast({
      title: "Error",
      description: "Failed to verify edit eligibility.",
      variant: "destructive",
    });
  }
};


  const handleDeleteExam = (exam) => {
    setExamToDelete(exam);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteExam = async () => {
    if (!examToDelete) return;

    setIsDeleting(true);
    try {
      await examsAPI.delete(examToDelete._id);
      toast({
        title: "Exam deleted",
        description: "The exam has been permanently deleted",
      });
      
      // Reset to page 1 and refetch to show updated list
      setPageMyExams(1);
      setPageCompanyExams(1);
      refetchAll();
      
      setIsDeleteDialogOpen(false);
      setExamToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete exam",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderExamTable = (exams, currentPage, sortField, sortOrder, handleSort) => {
    // Sort the exams
    const sortedExams = sortExams(exams, sortField, sortOrder);

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>S.No</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name
                  {getSortIcon('name', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('questions')}
              >
                <div className="flex items-center">
                  Questions
                  {getSortIcon('questions', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('totalTime')}
              >
                <div className="flex items-center">
                  Duration
                  {getSortIcon('totalTime', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('language')}
              >
                <div className="flex items-center">
                  Language
                  {getSortIcon('language', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('isTemplate')}
              >
                <div className="flex items-center">
                  Template
                  {getSortIcon('isTemplate', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('createdBy.name')}
              >
                <div className="flex items-center">
                  Created By
                  {getSortIcon('createdBy.name', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('createdDate')}
              >
                <div className="flex items-center">
                  Created On
                  {getSortIcon('createdDate', sortField, sortOrder)}
                </div>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedExams && sortedExams.length > 0 ? (
              sortedExams.map((exam, index) => (
                <TableRow key={exam._id}>
                  <TableCell className="font-medium">{(currentPage - 1) * limit + index + 1}</TableCell>
                  <TableCell className="font-medium">{exam.name}</TableCell>
                  <TableCell>{exam.questions?.length || 0}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1 text-gray-500" />
                      {exam.totalTime} min
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{exam.language || "English"}</Badge>
                  </TableCell>
                  <TableCell>
                    {exam.isTemplate ? (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                        Template
                      </Badge>
                    ) : (
                      "No"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">
                        {exam.createdBy?.name || "Unknown"}
                      </div>
                      <div className="text-gray-500">{exam.createdBy?.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {exam.createdDate
                      ? format(new Date(exam.createdDate), "MMM dd, yyyy")
                      : "N/A"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="View Exam"
                        onClick={() => handleViewExam(exam)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit Exam"
                        onClick={() => handleEditExam(exam)}
                      >
                        <FileEdit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteExam(exam)}
                        title="Delete Exam"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center p-4 text-gray-500">
                  No exams found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderPagination = (data, page, setPage) => {
    if (!data?.data?.totalPages || data.data.totalPages <= 1) return null;

    return (
      <Pagination className="mt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className={
                page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
              }
            />
          </PaginationItem>

          {[...Array(data.data.totalPages).keys()].map((i) => {
            const pageNumber = i + 1;
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  isActive={page === pageNumber}
                  onClick={() => setPage(pageNumber)}
                  className={`cursor-pointer ${
                    page === pageNumber
                      ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      : ""
                  }`}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          <PaginationItem>
            <PaginationNext
              onClick={() =>
                setPage((p) => Math.min(p + 1, data.data.totalPages))
              }
              disabled={page === data.data.totalPages}
              className={
                page === data.data.totalPages
                  ? "pointer-events-none opacity-50"
                  : "cursor-pointer"
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Exams</h1>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <PlusCircle className="w-4 h-4 mr-2" />
          Create New Exam
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Manage Exams</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            type="multiple"
            defaultValue={["my-exams", "company-exams"]}
            className="w-full"
          >
            {/* My Exams Section */}
            <AccordionItem value="my-exams">
              <AccordionTrigger className="text-lg font-semibold">
                Exams Created by You ({myExamsData?.data?.total || 0})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">

                  <form
                    onSubmit={handleSearchMy}
                    className="flex items-center space-x-2"
                  >
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                      <Input
                        type="search"
                        placeholder="Search my exams..."
                        className="pl-8 w-full"
                        value={searchTermMy}
                        onChange={(e) => setSearchTermMy(e.target.value)}
                      />
                    </div>
                    <Button type="submit">Search</Button>
                  </form>

                  {/* My Exams Table */}
                  {isLoadingMy ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
                    </div>
                  ) : (
                    <>
                      {renderExamTable(
                        myExamsData?.data?.exams, 
                        pageMyExams, 
                        mySortField, 
                        mySortOrder, 
                        handleMySort
                      )}
                      {renderPagination(
                        myExamsData,
                        pageMyExams,
                        setPageMyExams
                      )}
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Company Exams Section - keep existing code */}
            <AccordionItem value="company-exams">
              <AccordionTrigger className="text-lg font-semibold">
                Exams Created by Company ({companyExamsData?.data?.total || 0})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {/* Search for Company Exams */}
                  <form
                    onSubmit={handleSearchCompany}
                    className="flex items-center space-x-2"
                  >
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                      <Input
                        type="search"
                        placeholder="Search company exams..."
                        className="pl-8 w-full"
                        value={searchTermCompany}
                        onChange={(e) => setSearchTermCompany(e.target.value)}
                      />
                    </div>
                    <Button type="submit">Search</Button>
                  </form>

                  {/* Company Exams Table */}
                  {isLoadingCompany ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
                    </div>
                  ) : (
                    <>
                      {renderExamTable(
                        companyExamsData?.data?.exams, 
                        pageCompanyExams, 
                        companySortField, 
                        companySortOrder, 
                        handleCompanySort
                      )}
                      {renderPagination(
                        companyExamsData,
                        pageCompanyExams,
                        setPageCompanyExams
                      )}
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <AddExamModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          // Reset to page 1 to show newest exams first
          setPageMyExams(1);
          setPageCompanyExams(1);
          
          // Refetch both sections
          refetchAll();
          
          // Close modal
          setIsAddModalOpen(false);
          
          // Show success message
          toast({
            title: "Exam created",
            description: "The exam has been created successfully and appears at the top of the list",
          });
        }}
      />

      <ViewExamModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        exam={selectedExam}
      />

      <EditExamModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        examId={selectedExam?._id}
        onSuccess={() => {
          // Refetch both sections to show updated data
          refetchAll();
          
          // Close modal
          setIsEditModalOpen(false);
          
          // Show success message
          toast({
            title: "Exam updated",
            description: "The exam has been updated successfully",
          });
        }}
      />

      <DeleteExamConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDeleteExam}
        examName={examToDelete?.name}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Exams;
